import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '@/context/AppContext';
import { geminiService } from '@/services/geminiApi';
import { DesignError } from '@/types/api';
import styles from '@/styles/components/ResultStep.module.css';

export default function ResultStep() {
  const { t, i18n } = useTranslation();
  const { state, dispatch } = useApp();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const generateDesign = useCallback(async () => {
    if (!state.file) {
      setError(t('step3ErrorNotFound'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await geminiService.generateDesign({
        image: state.file,
        preferences: state.preferences,
        language: i18n.language as 'en' | 'zh',
      });

      dispatch({
        type: 'SET_RESULT',
        payload: {
          image: result.image,
          description: result.description,
        },
      });
    } catch (err) {
      console.error('Design generation error:', err);
      
      if (err instanceof DesignError) {
        switch (err.code) {
          case 'NETWORK_ERROR':
            setError(t('step3ErrorGeneric'));
            break;
          case 'API_ERROR':
            setError(t('step3ErrorGeneric'));
            break;
          case 'GENERATION_ERROR':
            setError(t('step3ErrorImage'));
            break;
          default:
            setError(t('step3ErrorGeneric'));
        }
      } else {
        setError(t('step3ErrorGeneric'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [state.file, state.preferences, i18n.language, t, dispatch]);

  const formattedDescription = useMemo(() => {
    if (!state.result?.description) return '';
    
    return state.result.description
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .split(/(\r\n|\n){2,}/)
      .map(paragraph => {
        const trimmed = paragraph.trim();
        if (trimmed === '') return '';
        
        if (trimmed.startsWith('### ')) {
          return trimmed.replace(/^### (.*)$/gim, '<h3>$1</h3>');
        }
        
        if (trimmed.startsWith('* ')) {
          const items = trimmed
            .split(/\r\n|\n/)
            .map(line => line.trim().replace(/^\* /, ''))
            .filter(Boolean)
            .map(item => `<li>${item}</li>`)
            .join('');
          return `<ul>${items}</ul>`;
        }
        
        return `<p>${trimmed.replace(/\r\n|\n/g, '<br />')}</p>`;
      })
      .join('');
  }, [state.result?.description]);

  const handleRestart = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, [dispatch]);

  const handleBack = useCallback(() => {
    dispatch({ type: 'SET_STEP', payload: 2 });
  }, [dispatch]);

  useEffect(() => {
    generateDesign();
  }, [generateDesign]);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <div className={styles.loadingText}>{t('step3Loading')}</div>
        <div className={styles.loadingSubtext}>{t('step3LoadingSubtext')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorText}>{error}</div>
        <div className={styles.buttonContainer}>
          <button className="button button-secondary" onClick={handleBack} style={{ marginRight: 'var(--spacing-md)' }}>
            {t('buttonBack')}
          </button>
          <button className="button" onClick={generateDesign}>
            重试 / Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.resultContainer}>
      <h2 className="step-title">{t('step3Title')}</h2>
      
      {state.result?.image && (
        <img 
          src={state.result.image} 
          alt="Generated Design" 
          className={styles.resultImage}
        />
      )}
      
      {formattedDescription && (
        <div 
          className={styles.resultDescription}
          dangerouslySetInnerHTML={{ __html: formattedDescription }}
        />
      )}
      
      <div className={styles.buttonContainer}>
        <button className="button button-secondary" onClick={handleRestart}>
          {t('buttonRestart')}
        </button>
      </div>
    </div>
  );
}